type AuthErrorEnvelope = {
  status?: number;
  code?: string;
  error?: string;
  message?: string;
  details?: Record<string, unknown>;
  retryAfterSeconds?: number;
};

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  AUTH_INVALID_CREDENTIALS:
    "Sign-in failed. Check your credentials and try again.",
  AUTH_METHOD_NOT_ALLOWED:
    "This sign-in method is not allowed for your organization policy.",
  TENANT_CONTEXT_MISMATCH:
    "This account is not assigned to this workspace. Sign in with a user for this organization, or clear site cookies and try again.",
  TENANT_ORG_MISMATCH:
    "This account is not assigned to this workspace. Sign in with a user for this organization, or clear site cookies and try again.",
  ORG_USER_NOT_ASSIGNED_SEAT:
    "Your organization has not assigned you a portal seat. Contact your organization administrator.",
  LICENSE_INACTIVE:
    "This organization does not have an active installation license. Please contact support.",
  LICENSE_STATE_MISSING:
    "Access is blocked because this organization's license state is unavailable. Restore the installation license state and try again.",
  LICENSE_SUSPENDED:
    "This installation's license has been suspended. Please contact your administrator.",
  LICENSE_READ_ONLY:
    "This installation is in read-only mode due to a license renewal failure. Data remains accessible, but changes are blocked.",
  LICENSE_CHECK_FAILED:
    "Unable to verify the installation license state. Please try again in a moment.",
  AUTH_MFA_INVALID: "Invalid authentication code. Try again.",
  AUTH_ACCOUNT_LOCKED: "Sign-in failed. Check your credentials and try again.",
  AUTH_POLICY_UNAVAILABLE:
    "Organization sign-in policy is unavailable. Please try again.",
  AUTH_SERVICE_ERROR:
    "Sign-in could not be completed. Please try again in a moment.",
  AUTH_RATE_LIMIT:
    "Too many sign-in requests were received. Please wait a moment and try again.",
  AUTH_SESSION_LIMIT: "Sign-in failed. Check your credentials and try again.",
  CAPTCHA_REQUIRED:
    "Additional verification is required before sign-in. Complete CAPTCHA and retry.",
  CAPTCHA_FAILED:
    "We could not verify the CAPTCHA challenge. Retry the challenge and try again.",
  CAPTCHA_ERROR:
    "CAPTCHA verification is temporarily unavailable. Please try again in a moment.",
};

export function resolveAuthErrorMessage(input: AuthErrorEnvelope): string {
  const code = typeof input.code === "string" ? input.code.trim() : "";
  if (code && AUTH_ERROR_MESSAGES[code]) {
    return AUTH_ERROR_MESSAGES[code];
  }

  const errorType = typeof input.error === "string" ? input.error.trim() : "";
  if (errorType === "PASSWORD_CHANGE_REQUIRED") {
    return "Password change is required before sign-in.";
  }
  const detailCode =
    input.details && typeof input.details.code === "string"
      ? input.details.code.trim()
      : "";
  if (detailCode && AUTH_ERROR_MESSAGES[detailCode]) {
    return AUTH_ERROR_MESSAGES[detailCode];
  }
  if (
    errorType === "Forbidden" &&
    (code === "AUTH_METHOD_NOT_ALLOWED" ||
      detailCode === "PASSWORD_LOGIN_DISABLED" ||
      detailCode === "AUTH_METHOD_NOT_ALLOWED")
  ) {
    return AUTH_ERROR_MESSAGES.AUTH_METHOD_NOT_ALLOWED;
  }
  if (errorType === "Locked") {
    return AUTH_ERROR_MESSAGES.AUTH_ACCOUNT_LOCKED;
  }

  if (code === "RateLimitExceeded" || input.status === 429) {
    const combinedText = `${errorType} ${input.message || ""}`.toLowerCase();
    const hasSessionLimitText =
      combinedText.includes("concurrent session") ||
      combinedText.includes("maximum concurrent");

    if (hasSessionLimitText) {
      return AUTH_ERROR_MESSAGES.AUTH_INVALID_CREDENTIALS;
    }

    if (
      typeof input.retryAfterSeconds === "number" &&
      Number.isFinite(input.retryAfterSeconds) &&
      input.retryAfterSeconds > 0
    ) {
      return `Too many sign-in requests were received. Please try again in ${formatRetryAfter(input.retryAfterSeconds)}.`;
    }

    return AUTH_ERROR_MESSAGES.AUTH_RATE_LIMIT;
  }

  if (input.status === 401 || input.status === 404) {
    return AUTH_ERROR_MESSAGES.AUTH_INVALID_CREDENTIALS;
  }
  if (input.status === 423) {
    return AUTH_ERROR_MESSAGES.AUTH_ACCOUNT_LOCKED;
  }
  if (input.status === 403) {
    // Prefer explicit auth-method denial only when coded as such; other 403s
    // (tenant mismatch, seat, license) must not look like a login-policy block.
    if (
      code === "AUTH_METHOD_NOT_ALLOWED" ||
      detailCode === "PASSWORD_LOGIN_DISABLED" ||
      detailCode === "AUTH_METHOD_NOT_ALLOWED"
    ) {
      return AUTH_ERROR_MESSAGES.AUTH_METHOD_NOT_ALLOWED;
    }
    if (code === "TENANT_CONTEXT_MISMATCH" || code === "TENANT_ORG_MISMATCH") {
      return AUTH_ERROR_MESSAGES.TENANT_ORG_MISMATCH;
    }
    return AUTH_ERROR_MESSAGES.AUTH_SERVICE_ERROR;
  }
  if (input.status === 503) {
    return AUTH_ERROR_MESSAGES.AUTH_POLICY_UNAVAILABLE;
  }
  if (typeof input.status === "number" && input.status >= 500) {
    return AUTH_ERROR_MESSAGES.AUTH_SERVICE_ERROR;
  }

  return AUTH_ERROR_MESSAGES.AUTH_SERVICE_ERROR;
}

function formatRetryAfter(seconds: number): string {
  const roundedSeconds = Math.max(1, Math.ceil(seconds));
  if (roundedSeconds < 60) {
    return `${roundedSeconds} second${roundedSeconds === 1 ? "" : "s"}`;
  }
  const minutes = Math.ceil(roundedSeconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}
